import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AcknowledgeModal, ActionModal, CardContent, Flex, Modal, Text } from '@percent/lemonade'
import { useMutation } from '@percent/utility'
import { useCurrencies } from '@percent/admin-dashboard/common/hooks'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { formatAmountFromMinorUnits } from '@percent/admin-dashboard/common/utility/formatAmount/formatAmount'
import { ManagerApproveBatchModalProps } from './ManagerApproveBatchModal.types'
import styles from './ManagerApproveBatchModal.module.scss'

export function ManagerApproveBatchModal({
  isOpen,
  onClose,
  disbursementBatchId,
  totalAmount,
  totalAmountCurrencyCode,
  totalDisbursementsCount,
  allBeneficiariesConfirmed,
  refreshData
}: Readonly<ManagerApproveBatchModalProps>) {
  const { t } = useTranslation()
  const { disbursementsService } = useServices()
  const { currencyInfo } = useCurrencies()

  const [status, setStatus] = useState<'error' | 'success' | null>(null)

  const [{ isPending }, { mutateAsync: managerApproveBatch }] = useMutation(disbursementsService.managerApproveBatch)

  const handleClose = () => {
    if (isPending) {
      return
    }

    setStatus(null)
    refreshData()
    onClose()
  }

  const handleSubmit = async () => {
    try {
      await managerApproveBatch(disbursementBatchId)
      setStatus('success')
    } catch {
      setStatus('error')
    }
  }

  const successModal = status === 'success' && (
    <AcknowledgeModal
      result="positive"
      title={t('dialog.managerApproveBatch.success.title')}
      buttonText={t('button.close')}
      handleClose={handleClose}
      viewTestId="manager-approve-batch-success-modal"
      buttonTestId="manager-approve-batch-success-modal-close"
    />
  )

  const errorModal = status === 'error' && (
    <AcknowledgeModal
      result="negative"
      title={t('dialog.managerApproveBatch.error.title')}
      buttonText={t('button.close')}
      handleClose={handleClose}
      viewTestId="manager-approve-batch-error-modal"
      buttonTestId="manager-approve-batch-error-modal-close"
    />
  )

  return (
    <Modal open={isOpen} onClose={handleClose}>
      {successModal || errorModal || (
        <ActionModal
          title={t('dialog.managerApproveBatch.title')}
          primaryButtonText={isPending ? t('button.submitting') : t('button.submit')}
          primaryBtnTestId="manager-approve-batch-modal-submit"
          secondaryButtonText={t('button.cancel')}
          secondaryBtnTestId="manager-approve-batch-modal-cancel"
          handleClose={handleClose}
          handleSubmit={handleSubmit}
          loading={isPending}
          disabled={!allBeneficiariesConfirmed}
        >
          <Flex direction="column" gap={16}>
            <Text as="p" size="small" color="gray600">
              {t('dialog.managerApproveBatch.description')}
            </Text>

            <Flex direction="column" gap={8}>
              <CardContent
                className={styles.cardContent}
                title={t('dialog.managerApproveBatch.totalAmount')}
                testId="manager-approve-batch-total-amount"
              >
                {formatAmountFromMinorUnits({
                  currencyCode: totalAmountCurrencyCode,
                  value: totalAmount,
                  currencies: currencyInfo
                })}
              </CardContent>

              <CardContent
                className={styles.cardContent}
                title={t('dialog.managerApproveBatch.totalDisbursements')}
                testId="manager-approve-batch-total-disbursements"
              >
                {totalDisbursementsCount}
              </CardContent>
            </Flex>

            <Text as="p" size="small" color="gray600">
              {t('dialog.managerApproveBatch.confirmation')}
            </Text>
          </Flex>
        </ActionModal>
      )}
    </Modal>
  )
}
